/**
 * Extension Manager
 *
 * Resolves which extensions are active for a given board / flow, and
 * merges their field + action definitions so the UI can render them.
 *
 * Enabled extensions are stored on the board/flow primitive under
 *   referenceParameters.extensions = { company_finances: true, ... }
 * or
 *   getConfig.extensions = { company_finances: true, ... }
 *
 * If neither is present all extensions are treated as disabled (opt-in).
 */

import { getAllExtensions } from "./registry"

// ---------------------------------------------------------------------------
// Resolve enabled extensions for a board or flow
// ---------------------------------------------------------------------------

/**
 * Read the set of enabled extension ids from a board/flow primitive.
 * @param {Object}  boardOrFlow - A MainStore primitive representing a board or flow.
 * @returns {Set<string>}       - Set of enabled extension ids.
 */
export function getEnabledExtensionIds(boardOrFlow) {
  if (!boardOrFlow) return new Set()

  // Prefer referenceParameters.extensions, fall back to getConfig.extensions
  const extMap =
    boardOrFlow.referenceParameters?.extensions ??
    boardOrFlow.getConfig?.extensions ??
    {}

  if (typeof extMap !== "object" || Array.isArray(extMap)) return new Set()

  const enabled = new Set()
  for (const [id, flag] of Object.entries(extMap)) {
    if (flag) enabled.add(id)
  }
  return enabled
}

/**
 * Return the full ExtensionDef objects that are currently enabled.
 * @param {Object} boardOrFlow
 * @returns {ExtensionDef[]}
 */
export function getEnabledExtensions(boardOrFlow) {
  const enabled = getEnabledExtensionIds(boardOrFlow)
  if (enabled.size === 0) return []

  return getAllExtensions().filter((ext) => enabled.has(ext.id))
}

// ---------------------------------------------------------------------------
// Resolve *applicable* extensions for a specific primitive
// ---------------------------------------------------------------------------

/**
 * Check whether a given extension applies to the supplied primitive.
 * An extension applies when:
 *   - Its forCategories includes the primitive's referenceId (or is empty/undefined)
 *   - Its forTypes includes the primitive's type (or is empty/undefined)
 *
 * @param {ExtensionDef} ext
 * @param {Object}       primitive - A MainStore primitive.
 * @returns {boolean}
 */
function extensionAppliesTo(ext, primitive) {
  if (!primitive) return false

  // Category check
  if (Array.isArray(ext.forCategories) && ext.forCategories.length > 0) {
    if (!ext.forCategories.includes(primitive.referenceId)) {
      return false
    }
  }

  // Type check
  if (Array.isArray(ext.forTypes) && ext.forTypes.length > 0) {
    if (!ext.forTypes.includes(primitive.type)) {
      return false
    }
  }

  return true
}

/**
 * Given an array of enabled extensions and a primitive, return the subset
 * that actually applies to that primitive.
 *
 * @param {ExtensionDef[]} extensions
 * @param {Object}         primitive
 * @returns {ExtensionDef[]}
 */
export function getApplicableExtensions(extensions, primitive) {
  if (!Array.isArray(extensions) || extensions.length === 0) return []
  return extensions.filter((ext) => extensionAppliesTo(ext, primitive))
}

// ---------------------------------------------------------------------------
// Sidebar field injection
// ---------------------------------------------------------------------------

/**
 * Build a merged parameter-like map from the applicable extensions' sidebarFields.
 *
 * The returned object mimics the shape of `metadata.parameters` so it can be
 * fed directly into the existing `buildFieldList` / `FieldList` rendering path:
 *
 *   {
 *     "financialData.totalRevenue": { title: "Total Revenue", type: "currency", order: 0 },
 *     ...
 *   }
 *
 * @param {ExtensionDef[]} extensions
 * @param {Object}         primitive
 * @returns {Object}       - Parameter map suitable for buildFieldList.
 */
export function buildExtensionParameters(extensions, primitive) {
  if (!Array.isArray(extensions) || extensions.length === 0) return {}
  const applicable = getApplicableExtensions(extensions, primitive)
  if (applicable.length === 0) return {}

  const params = {}
  for (let extIdx = 0; extIdx < applicable.length; extIdx++) {
    const ext = applicable[extIdx]
    const fields = ext.sidebarFields ?? []
    for (let fIdx = 0; fIdx < fields.length; fIdx++) {
      const field = fields[fIdx]
      if (!field || !field.key) continue
      // Preserve any existing param config and overlay extension definition.
      // Extension definition takes priority for title/type but keeps existing extras.
      const existing = params[field.key] ?? {}
      params[field.key] = {
        ...existing,
        title: field.title ?? existing.title ?? field.key,
        type: field.type ?? existing.type ?? "string",
        order: field.order ?? existing.order ?? (extIdx * 100 + fIdx + 200), // place after built-in fields
        extension: ext.id,
      }
    }
  }
  return params
}

/**
 * Merge extension parameters into an existing parameters map.
 * Extension definitions are added only for keys that don't already exist,
 * avoiding overriding core metadata definitions while still injecting new fields.
 *
 * @param {Object}         baseParams  - The primitive's metadata.parameters (or resolved parameters).
 * @param {ExtensionDef[]} extensions
 * @param {Object}         primitive
 * @returns {Object}       - Merged parameter map.
 */
export function mergeExtensionParameters(baseParams, extensions, primitive) {
  const extParams = buildExtensionParameters(extensions, primitive)
  // Base params take priority — only fill in missing keys.
  const merged = { ...extParams }
  if (baseParams) {
    Object.entries(baseParams).forEach(([key, config]) => {
      merged[key] = config
    })
  }
  return merged
}

// ---------------------------------------------------------------------------
// Action injection
// ---------------------------------------------------------------------------

/**
 * Build an array of action menu items from the applicable extensions' actionItems.
 *
 * Each action item is a plain object compatible with `PrimitiveCard.CardMenu`:
 *
 *   {
 *     title: "Convert Financials",
 *     icon: "FAMoney",
 *     action: async () => { await MainStore().doPrimitiveAction(primitive, extAction.key) },
 *     _extension: true,
 *   }
 *
 * @param {ExtensionDef[]} extensions
 * @param {Object}         primitive
 * @param {Object}         [mainstore] - MainStore singleton (used for doPrimitiveAction).
 * @returns {Object[]}
 */
export function buildExtensionActionItems(extensions, primitive, mainstore) {
  if (!Array.isArray(extensions) || extensions.length === 0) return []
  const applicable = getApplicableExtensions(extensions, primitive)
  if (applicable.length === 0) return []

  const items = []
  for (const ext of applicable) {
    const actions = ext.actionItems ?? []
    for (const act of actions) {
      if (!act || !act.key) continue
      items.push({
        ...act,
        _extension: true,
        _extensionId: ext.id,
        action: act.action
          ? act.action
          : mainstore
            ? async () => {
                await mainstore.doPrimitiveAction(primitive, act.key)
              }
            : undefined,
      })
    }
  }
  return items
}

// ---------------------------------------------------------------------------
// Storage helpers (for the configuration UI)
// ---------------------------------------------------------------------------

/**
 * Toggle a single extension on/off on the board/flow, persisting via setField.
 *
 * @param {Object}  boardOrFlow
 * @param {string}  extensionId
 * @param {boolean} enabled
 * @returns {Promise<void>}
 */
export async function toggleExtension(boardOrFlow, extensionId, enabled) {
  if (!boardOrFlow || !extensionId) return
  const current = { ...(boardOrFlow.referenceParameters?.extensions ?? {}) }
  if (enabled) {
    current[extensionId] = true
  } else {
    delete current[extensionId]
  }
  await boardOrFlow.setField("referenceParameters.extensions", current)
}

/**
 * Return a canonical plain object representing the extension toggles
 * (suitable for feeding into a React checkbox list).
 *
 * @param {Object} boardOrFlow
 * @returns {Object}  e.g. { company_finances: true, sbti_targets: false }
 */
export function getExtensionToggles(boardOrFlow) {
  const enabled = getEnabledExtensionIds(boardOrFlow)
  const toggles = {}
  for (const ext of getAllExtensions()) {
    toggles[ext.id] = enabled.has(ext.id)
  }
  return toggles
}
